import { Table } from 'react-bootstrap';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { formatMoney, formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { ARCHETYPE_LABEL, AssetSeries } from './metaHackathon-constants';
import {
  AdPerformanceMetrics,
  AdPerformanceRow,
  PERFORMANCE_COLUMNS,
  PerformanceColumnKey
} from './metaHackathon-performance';
import { campaignForAsset, quadrantOf } from './metaHackathon-utils';

const formatMetric = (
  metrics: AdPerformanceMetrics | undefined,
  col: (typeof PERFORMANCE_COLUMNS)[number]
): string => {
  if (!metrics) return '—';
  return col.isMoney ? formatMoney(metrics[col.key]) : formatNumber(metrics[col.key]);
};

interface MetaDataTableProps {
  series: AssetSeries[];
  performance?: Map<string, AdPerformanceRow>;
  visibleCols: Record<PerformanceColumnKey, boolean>;
  onToggleCol: (key: PerformanceColumnKey) => void;
}

export const MetaDataTable = ({
  series,
  performance,
  visibleCols,
  onToggleCol
}: MetaDataTableProps) => {
  const activeCols = PERFORMANCE_COLUMNS.filter(col => visibleCols[col.key]);

  // One row per asset × date, sorted earliest → latest (then by ad name).
  const rows = series
    .flatMap(s => s.points.map(point => ({ s, point })))
    .sort(
      (a, b) =>
        a.point.date.localeCompare(b.point.date) || a.s.assetName.localeCompare(b.s.assetName)
    );

  return (
    <div className='meta-data-table'>
      <div className='col-toggles'>
        <span className='col-toggles-label'>Performance columns</span>
        {PERFORMANCE_COLUMNS.map(col => (
          <ToggleSwitch
            key={col.key}
            label={col.label}
            checked={visibleCols[col.key]}
            onChange={() => onToggleCol(col.key)}
          />
        ))}
      </div>
      <div className='table-scroll'>
        <Table responsive hover size='sm'>
          <thead>
            <tr>
              <th>Date</th>
              <th>Campaign</th>
              <th>Ad</th>
              <th>Diversity trend</th>
              <th className='numeric'>Fatigue</th>
              <th className='numeric'>Diversity</th>
              <th>Quadrant</th>
              {activeCols.map(col => (
                <th key={col.key} className='numeric'>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ s, point }) => {
              const metrics = performance?.get(s.assetId);
              return (
                <tr key={`${s.assetId}-${point.date}`}>
                  <td>{point.date}</td>
                  <td>{campaignForAsset(s, performance)}</td>
                  <td>{s.assetName}</td>
                  <td>{ARCHETYPE_LABEL[s.archetype]}</td>
                  <td className='numeric'>{point.fatigue.toFixed(2)}</td>
                  <td className='numeric'>{point.diversity.toFixed(2)}</td>
                  <td>{quadrantOf(point.fatigue, point.diversity)}</td>
                  {activeCols.map(col => (
                    <td key={col.key} className='numeric'>
                      {formatMetric(metrics, col)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </div>
  );
};
